import React from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { groupBy, sortBy } from 'lodash';
import TempCostCorrelationChart from './TempCostCorrelationChart';
import TempCostTimelineChart from './TempCostTimelineChart';
import ForecastTable from './ForecastTable';

// Helper for color palette
const COLORS = [
  '#8884d8', '#82ca9d', '#ff7300', '#ff0000', '#00bcd4', '#ffb300', '#e91e63', '#009688', '#8bc34a', '#ffc107', '#607d8b', '#795548'
];

// Data format: [{ forecast_date, target_date, hour, lrz4_mtl_forecast, lrz4_actual }]
function ForecastChart({ data, targetDate, dataType, forecastWindow, monthlyHigh, theme }) {
  // Always declare hooks at the top
  const [highlight, setHighlight] = React.useState(null);

  if (!data || data.length === 0) return <p>No data available for this selection.</p>;

  // Check if actuals are available for the selected date
  const actualsExist = data.some(d => d.actuals !== null && d.actuals !== undefined);

  // For any date (including 'all'), we want to show all power forecasts made for that date
  // Group by forecast_date, each line is a power forecast made on a specific day
  const grouped = groupBy(data, 'forecast_date');
  const hours = Array.from(new Set(data.map(d => d.hour))).sort((a, b) => a - b);
  // Ensure 'latest' is always last and labeled as such, and filter out 'actuals' as a forecast line
  let forecastDates = Object.keys(grouped)
    .filter(date => date !== 'actuals')
    .sort((a, b) => {
      if (a === 'latest') return 1;
      if (b === 'latest') return -1;
      return a.localeCompare(b);
    });

  // Log what we have for debugging
  console.log(`Chart data for ${targetDate}: ${data.length} rows, ${forecastDates.length} forecast dates`);
  forecastDates.forEach(date => {
    console.log(`- Forecast from ${date}: ${grouped[date].length} hours`);
  });

  // Build data for chart: one row per hour, each forecast_date as a key
  // First deduplicate data by hour for each forecast date
  const deduplicatedGrouped = {};
  forecastDates.forEach(date => {
    deduplicatedGrouped[date] = {};
    grouped[date].forEach(item => {
      if (!deduplicatedGrouped[date][item.hour] || 
          (item.forecast !== null && deduplicatedGrouped[date][item.hour].forecast === null)) {
        deduplicatedGrouped[date][item.hour] = item;
      }
    });
  });

  const chartData = hours.map(hour => {
    const row = { hour };
    forecastDates.forEach(date => {
      // Only use forecast/actual, ignore temperature
      const found = deduplicatedGrouped[date][hour];
      row[date] = found ? found.forecast : null;
      row[`actuals_${date}`] = found ? found.actuals : null;
    });
    // Try to get the actual from any entry for this hour (they should all have the same actual)
    const hourEntries = data.filter(d => d.hour === hour);
    const actualEntry = hourEntries.find(d => d.actuals !== null && d.actuals !== undefined);
    row.actuals = actualEntry ? actualEntry.actuals : null;
    return row;
  });
  
  // For the error chart, plot actual as a horizontal line at y=0, and each forecast as (forecast - actual)
  const diffData = chartData.map(row => {
    const diffs = {};
    forecastDates.forEach(date => {
      const forecast = row[date];
      const actuals = row.actuals;
      let diff = forecast !== null && actuals !== null ? forecast - actuals : null;
      if (diff !== null) diff = Math.round(diff * 100) / 100;
      diffs[date] = diff;
    });
    return {
      hour: row.hour,
      ...diffs,
      actuals: 0 // always zero baseline for actual
    };
  });

  // Custom tooltip for error chart
  const ErrorTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isDark = document.body.classList.contains('dark-mode');
      return (
        <div style={{
          background: isDark ? '#23272a' : '#fff',
          color: isDark ? '#fff' : '#222',
          border: '1px solid ' + (isDark ? '#444' : '#ccc'),
          borderRadius: 6,
          padding: 10,
          fontSize: 14,
          boxShadow: isDark ? '0 2px 8px #111' : '0 2px 8px #ccc',
          minWidth: 120
        }}>
          <div style={{ marginBottom: 4, fontWeight: 600 }}>Hour: {label}</div>
          {payload.map((entry, idx) => (
            <div key={idx} style={{ color: entry.color, fontWeight: entry.dataKey === 'actuals' ? 700 : 400 }}>
              {entry.name}: <b>{entry.value}</b>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const labelType = dataType === 'miso' ? 'MISO' : 'LRZ4';
  return (
    <div>
      <h2>{labelType} Power Forecasts by Hour ({targetDate === 'all' ? 'Past Week' : `Up to ${targetDate}`})</h2>
      {!actualsExist && targetDate !== 'all' && (
        <p style={{ color: 'gray', fontStyle: 'italic' }}>
          No actuals available for this date yet. Only power forecasts are shown.
        </p>
      )}
      <ResponsiveContainer width="100%" height={500}>
        <LineChart data={chartData} margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
          <XAxis 
            dataKey="hour" 
            label={{ value: 'Hour Ending', position: 'insideBottomRight', offset: 0 }} 
            ticks={[1, 4, 8, 12, 16, 20, 24]}
            tickFormatter={(hour) => `${hour}`}
          />
          <YAxis label={{ value: 'MW', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={({ active, payload, label }) => {
            if (!active || !payload || !payload.length) return null;
            // Detect dark mode from body
            const isDark = document.body.classList.contains('dark-mode');
            return (
              <div
                style={{
                  background: isDark ? '#23272a' : '#fff',
                  color: isDark ? '#fff' : '#222',
                  border: '1px solid #444',
                  padding: 12,
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  minWidth: 180
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 700, opacity: 0.85, marginBottom: 6, color: isDark ? '#fff' : '#888' }}>
                  Hour: {label}
                </div>
                {payload.map((entry, idx) => (
                  <div key={idx} style={{ color: entry.color, fontWeight: 500, marginBottom: 2 }}>
                    {entry.name}: <b>{entry.value}</b>
                  </div>
                ))}
              </div>
            );
          }} />
          <Legend onMouseEnter={e => setHighlight(e.dataKey)} onMouseLeave={() => setHighlight(null)} />
          {forecastDates.map((date, idx) => (
            <Line
              key={date}
              type="monotone"
              dataKey={date}
              name={date}
              stroke={date === '__final_forecast__' ? '#ff0000' : COLORS[idx % COLORS.length]}
              dot={false}
              isAnimationActive={false}
              strokeWidth={highlight === date ? 4 : 1}
              opacity={highlight && highlight !== date ? 0.3 : 1}
            />
          ))}
          {actualsExist && <Line type="monotone" dataKey="actuals" name="Actual" stroke={document.body.classList.contains('dark-mode') ? '#fff' : '#222'} dot={false} strokeWidth={2} />}
        </LineChart>
      </ResponsiveContainer>
      <h3 style={{ marginTop: 32 }}>{labelType} Power Forecast Error vs Actual (Actual is Baseline)</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={diffData} margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
          <XAxis 
            dataKey="hour" 
            ticks={[1, 4, 8, 12, 16, 20, 24]}
            tickFormatter={(hour) => `${hour}`}
          />
          <YAxis label={{ value: 'MW Diff', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={<ErrorTooltip />} />
          <Legend onMouseEnter={e => setHighlight(e.dataKey)} onMouseLeave={() => setHighlight(null)} />
          {forecastDates.map((date, idx) => (
            <Line
              key={date}
              type="monotone"
              dataKey={date}
              name={date}
              stroke={COLORS[idx % COLORS.length]}
              dot={false}
              isAnimationActive={false}
              strokeWidth={highlight === date ? 3 : 1}
              opacity={highlight && highlight !== date ? 0.3 : 1}
            />
          ))}
          <Line type="monotone" dataKey="actuals" name="Actual (Baseline)" stroke={document.body.classList.contains('dark-mode') ? '#fff' : '#222'} dot={false} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
      {/* Only render ForecastTable ONCE, below the chart, for the selected date */}
      {targetDate !== 'all' && (
        <ForecastTable data={data} targetDate={targetDate} dataType={dataType} forecastWindow={forecastWindow} monthlyHigh={monthlyHigh} theme={theme} />
      )}
    </div>
  );
}

export default ForecastChart;
